/**
 * Traduction globale de l'interface à l'exécution.
 *
 * L'admin a été majoritairement codé en dur en anglais (seuls 2 fichiers sur
 * ~97 utilisent `$t()`). Plutôt que de réécrire tous les templates — long et
 * risqué — ce plugin traduit les textes visibles via un dictionnaire « liste
 * blanche » : seules les chaînes anglaises EXACTES présentes dans le
 * dictionnaire de la langue courante sont remplacées. Les données dynamiques
 * (noms de produits, e-mails, prix, ids...) ne correspondent à aucune clé et
 * ne sont donc jamais touchées.
 *
 * Le dictionnaire d'une langue est l'objet `i18n.messages[locale]` : ses clés
 * sont les chaînes sources anglaises, ses valeurs les traductions. `en.json`
 * sert de référence (clés = anglais) et définit l'ensemble des chaînes
 * traduisibles (« knownSource »).
 *
 * Fonctionnement :
 *  - un MutationObserver sur <body> traduit les nœuds ajoutés / modifiés ;
 *  - un passage complet est rejoué au montage, à chaque changement de route et
 *    à chaque changement de langue ;
 *  - l'anglais source de chaque nœud traduit est mémorisé (WeakMap) afin de
 *    pouvoir basculer de langue sans perdre le texte d'origine, tout en
 *    respectant les valeurs dynamiques (on ne mémorise que les nœuds dont le
 *    texte est une clé connue).
 */

const TEXT_ATTRS = ['placeholder', 'title', 'alt'];

let i18nRef = null;
let knownSource = new Set();
let observer = null;
let rafId = null;
let pendingFull = true;
let pendingNodes = [];

const textOriginals = new WeakMap();  // Text node  -> anglais source
const attrOriginals = new WeakMap();  // Element    -> { attr: anglais source }

function currentDict() {
    if (!i18nRef) return null;
    if (i18nRef.locale === 'en') return null; // source = anglais, rien à traduire
    return i18nRef.messages[i18nRef.locale] || null;
}

function translateValue(source, map) {
    if (!map) return source;
    const key = String(source).trim();
    if (!key) return source;
    const val = map[key];
    if (typeof val === 'string' && val !== key) {
        return String(source).replace(key, val);
    }
    return source;
}

function processTextNode(node) {
    const v = node.nodeValue;
    if (v == null) return;
    const vt = v.trim();
    const map = currentDict();
    const stored = textOriginals.get(node);

    let source;
    if (vt && knownSource.has(vt)) {
        // Texte anglais connu (posé par Vue ou non encore traduit) : c'est la source.
        source = v;
        if (stored !== v) textOriginals.set(node, v);
    } else if (stored !== undefined) {
        // Le nœud affiche déjà une traduction : on repart de l'anglais mémorisé.
        source = stored;
    } else {
        // Texte dynamique / inconnu : on n'y touche jamais.
        return;
    }

    if (!map) {
        // Langue anglaise : restaure l'anglais source si le nœud était traduit.
        if (stored !== undefined && v !== stored) node.nodeValue = stored;
        return;
    }
    const target = translateValue(source, map);
    if (v !== target) node.nodeValue = target;
}

function applyAttr(el, attr, source, map) {
    const out = translateValue(source, map);
    if (el.getAttribute(attr) !== out) el.setAttribute(attr, out);
}

function processAttrs(el) {
    if (!el || el.nodeType !== 1) return;
    const map = currentDict();
    let stored = attrOriginals.get(el);
    for (const attr of TEXT_ATTRS) {
        if (!el.hasAttribute(attr)) continue;
        const v = el.getAttribute(attr) || '';
        const vt = v.trim();
        if (vt && knownSource.has(vt)) {
            if (!stored) { stored = {}; attrOriginals.set(el, stored); }
            stored[attr] = v;
            applyAttr(el, attr, v, map);
        } else if (stored && stored[attr] !== undefined) {
            applyAttr(el, attr, stored[attr], map);
        }
    }
}

function walk(root) {
    if (!root) return;
    if (root.nodeType === 3) { processTextNode(root); return; }
    if (root.nodeType !== 1 && root.nodeType !== 9 && root.nodeType !== 11) return;
    if (root.nodeType === 1) processAttrs(root);
    // Nœuds texte
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let n = walker.nextNode();
    while (n) { processTextNode(n); n = walker.nextNode(); }
    // Attributs des descendants
    if (root.querySelectorAll) {
        const els = root.querySelectorAll('[' + TEXT_ATTRS.join('],[') + ']');
        for (let i = 0; i < els.length; i++) processAttrs(els[i]);
    }
}

function runPass() {
    rafId = null;
    if (pendingFull) {
        pendingFull = false;
        pendingNodes = [];
        walk(document.body);
        return;
    }
    const nodes = pendingNodes;
    pendingNodes = [];
    for (let i = 0; i < nodes.length; i++) walk(nodes[i]);
}

function schedule(node) {
    if (node) pendingNodes.push(node);
    if (rafId == null) rafId = requestAnimationFrame(runPass);
}

function scheduleFull() {
    pendingFull = true;
    pendingNodes = [];
    if (rafId == null) rafId = requestAnimationFrame(runPass);
}

function onMutation(mutations) {
    if (pendingFull) return;
    for (const m of mutations) {
        if (m.type === 'childList') {
            for (let i = 0; i < m.addedNodes.length; i++) schedule(m.addedNodes[i]);
        } else if (m.type === 'characterData') {
            if (m.target && m.target.parentNode) schedule(m.target.parentNode);
        } else if (m.type === 'attributes') {
            if (m.target) schedule(m.target);
        }
    }
}

/**
 * Initialise la traduction globale.
 * @param {VueI18n} i18n instance VueI18n (fournit locale + messages)
 * @param {VueRouter} [router] routeur pour rejouer un passage à chaque navigation
 */
export function setupAutoI18n(i18n, router) {
    i18nRef = i18n;
    knownSource = new Set(Object.keys((i18n.messages && i18n.messages.en) || {}));

    const applyDoc = () => {
        const loc = i18n.locale || 'en';
        if (typeof document !== 'undefined' && document.documentElement) {
            document.documentElement.setAttribute('lang', loc);
        }
    };

    applyDoc();
    // Rejoue un passage complet à chaque changement de langue.
    if (typeof i18n.watchI18n === 'function') { /* noop, garde-fou */ }
    const start = () => {
        if (typeof document === 'undefined') return;
        scheduleFull();
        if (!observer && typeof MutationObserver !== 'undefined') {
            observer = new MutationObserver(onMutation);
            observer.observe(document.body, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true,
                attributeFilter: TEXT_ATTRS
            });
        }
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }

    if (router && typeof router.afterEach === 'function') {
        router.afterEach(() => { applyDoc(); scheduleFull(); });
    }

    // Surveille le changement de langue (locale est une propriété réactive).
    try {
        const unwatch = i18n._vm && i18n._vm.$watch
            ? i18n._vm.$watch(() => i18n.locale, () => { applyDoc(); scheduleFull(); })
            : null;
        if (!unwatch && typeof setInterval === 'function') {
            let last = i18n.locale;
            setInterval(() => { if (i18n.locale !== last) { last = i18n.locale; applyDoc(); scheduleFull(); } }, 400);
        }
    } catch (e) { /* noop */ }
}

export default { install(Vue, options) { if (options && options.i18n) setupAutoI18n(options.i18n, options.router); } };
